import RunConfigService from './runConfigService';

/**
 *
 * UrlService
 *
 * @namespace UrlService
 */
export default {
    extractHostURL : extractHostURL,
    extractDomain: extractDomain,
    getApisUrl,
    getAwsUrl,
    getSiteUrl,
    getSelectors: getSelectors,
    getLocaleUrl: getLocaleUrl,
    getAemLocaleUrl,
    getLocale: getLocale,
    getLocaleFromUrl,
    isLocalePage: isLocalePage,
    isAuthor: isAuthor,
    isMeCommerce: isMeCommerce,
    isMeCommerceStorefront: isMeCommerceStorefront,
    isWCMDisabled: isWCMDisabled,
    checkUrlForExtension: checkUrlForExtension,
    toQueryString
};

function extractHostURL(url) {
    if (isAuthor()) {
        // NOTE: changed this line because in case of local host with port this function was producing urls like:
        // http://localhost:4502/4502/apps/hello/services/regions.storefront.json ('double porting')
        let parsedURL = url.split(window.location.host);
        if (parsedURL.length === 2) {
            return window.location.protocol + "//" + window.location.host + parsedURL[1];
        }
    }

    return url;
}

function extractDomain(url = window.location.href) {
    // Remove protocol if present, and extract domain from url
    let domain = url.indexOf("://") > -1 ? url.split('/')[2] : url.split('/')[0];

    // Remove port number from domain if present
    domain = domain.split(':')[0];

    return domain;
}

function getSelectors(url) {
    let page = url.split('/').pop();
    let selectors = page.split('.');
    selectors.pop();
    selectors.shift();
    return selectors;
}

function getLocaleUrl() {
    let fullPath = window.location.pathname,
        localeString = '/' + getLocale() + '/';

    return fullPath.indexOf(localeString) > -1 ? fullPath.split(localeString)[0] + localeString : fullPath;
}

/**
 * This just builds the standard AEM path up through the locale and does not look
 * at the current href.  This takes into account the JP market path difference.
 *
 * @return {string}
 */
function getAemLocaleUrl() {
    const secondPart = RunConfigService.getRunConfig().country === 'JP' ? 'markets' : 'nuskin';
    return `/content/${secondPart}/${getLocale()}/`;
}

function getLocaleFromUrl(url = window.location.href) {
    let language, country, locale;
    let href_locale = url.match(/\/[a-z]{2}_[A-Z]{2}\//);
    if (href_locale) {
        locale = href_locale[0].substring(1, href_locale[0].length-1);
        if (locale) {
            [language, country] = locale.split('_');
        }
    } else {
        href_locale = url.match(/\/([a-z]|[A-Z]){2}\/([a-z]|[A-Z]){2}\//);
        if (href_locale) {
            locale = href_locale[0].substring(1, href_locale[0].length-1);
            if (locale) {
                [country, language] = locale.split('/');
                country = country.toUpperCase();
                locale = `${language}_${country.toUpperCase()}`;
            }
        }
    }
    return locale ? [language, country, locale] : [];

}

/**
 * Tests to see if the locale is part of the URL.
 * Note: It appears the default locale is en_US even if there is no locale in the path.
 * @returns {boolean}
 */
function isLocalePage() {
    const url = window.location.href;
    const lowerUrl = window.location.href.toLowerCase();
    return url.includes(`/${getLocale()}/`) || lowerUrl.includes(`/${getEqLocale()}/`);
}

function isAuthor() {
    // This function has a known flaw for AEM instances running locally with an Apache server:
    // In such cases, this function cannot distinguish between Author mode and Preview mode
    return (window.location.port === "4502" ||
        window.location.hostname.indexOf("auth") !== -1 ||
        !isWCMDisabled());
}

function isMeCommerce() {
    return window.location.href.indexOf('mecommerce') > -1;
}

function isMeCommerceStorefront() {
    // The page is considered part of MeCommerce Storefront
    // if the url does not begin with dev., test., or www.
    let domain = extractDomain(window.location.href);
    return !domain.startsWith('dev.') &&
        !domain.startsWith('test.') &&
        !domain.startsWith('www.') &&
        domain.indexOf('.mynuskin.') >= 0;  // Without this line you can't run in localhost mode or it will think it is storefront
}

function isWCMDisabled() {
    return (typeof(CQ) === 'undefined' ||
        typeof(CQ.WCM) === 'undefined' ||
        CQ.WCM.isPreviewMode());
}

function getLocale() {
    let runConfig = RunConfigService.getRunConfig();
    return `${runConfig.language}_${runConfig.country}`;
}

function getEqLocale() {
    let runConfig = RunConfigService.getRunConfig();

    return `${runConfig.country.toLowerCase()}/${runConfig.language.toLowerCase()}`;
}

function checkUrlForExtension(url) {
    // only add .html to url path if it doesn't start with http or is the #
    if (url && !url.startsWith("http") && "#" !== url && !url.includes(".html")) {
        url += ".html";
    }

    return url;
}

/**
 * Creates a URI query string from the given JSON object. The object must only contain
 * values that are strings, numbers, or boolean values. Any other value type may result
 * in undefined query behavior.
 * @example toQueryString({foo: 9000, bar: false, baz: 'escaped "string": {}()%$!?'}) // "foo=9000&baz=escaped%20%22string%22%3A%20%7B%7D()%25%24!%3F"
 * @param {Object<string, string | number | boolean>} obj - JSON Object to convert
 * @returns {string} Assembled query string
 */
function toQueryString(obj) {
    return Object.keys(obj)
        .reduce((arr, key) => {
            if (obj[key]) {
                arr.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`);
            }
            return arr;
        }, [])
        .join('&');
}

/**
 * Returns the aws cloud url based on environment
 */
function getAwsUrl() {
    let env = RunConfigService.getEnvironmentCode();
    env = env === 'prod' ? '' : env;
    return `https://${env}api.cloud.nuskin.com`;
}

/**
 * Returns the aws cloud url based on environment
 */
function getApisUrl() {
    let env = RunConfigService.getEnvironmentCode();
    env = env === 'prod' ? '' : `${env}.`;
    return `https://apis.${env}nuskin.com`;
}

function getSiteUrl() {
    let env = RunConfigService.getEnvironmentCode();
    env = env === 'prod' ? 'www' : env;
    return `https://${env}.nuskin.com`;
}
